/**
 * Browser-native cryptography utilities.
 *
 * - sha256()       — one-way hash for anonymising identifiers sent to analytics
 * - SecureStorage  — AES-GCM encrypted localStorage wrapper for sensitive state
 *
 * All operations use the Web Crypto API (no third-party deps).
 */

// ── SHA-256 hash ──────────────────────────────────────────────────────────────

/** Return the SHA-256 hex digest of a string. Used to anonymise user IDs. */
export async function sha256(input: string): Promise<string> {
  const buf = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(input),
  )
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// ── Encrypted localStorage ────────────────────────────────────────────────────

const SESSION_KEY_NAME = '__bb_sk'   // session key lives in sessionStorage
const IV_LENGTH = 12                  // bytes — AES-GCM standard

/**
 * Get (or lazily create) a session-scoped AES-GCM key.
 *
 * The raw key material is stored in sessionStorage so it survives page refreshes
 * within the same browser tab but is discarded when the tab is closed.  Data
 * encrypted with one session's key cannot be read in a future session, which is
 * intentional: stale financial data should not persist indefinitely.
 */
async function _getSessionKey(): Promise<CryptoKey> {
  const stored = sessionStorage.getItem(SESSION_KEY_NAME)

  let rawKey: Uint8Array
  if (stored) {
    rawKey = Uint8Array.from(atob(stored), (c) => c.charCodeAt(0))
  } else {
    rawKey = crypto.getRandomValues(new Uint8Array(32))
    sessionStorage.setItem(SESSION_KEY_NAME, btoa(String.fromCharCode(...rawKey)))
  }

  return crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ])
}

async function _encrypt(plaintext: string): Promise<string> {
  const key = await _getSessionKey()
  const iv  = crypto.getRandomValues(new Uint8Array(IV_LENGTH))
  const enc = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(plaintext),
  )
  // Prepend IV to ciphertext then base64-encode the whole thing
  const combined = new Uint8Array(IV_LENGTH + enc.byteLength)
  combined.set(iv)
  combined.set(new Uint8Array(enc), IV_LENGTH)
  return btoa(String.fromCharCode(...combined))
}

async function _decrypt(ciphertext: string): Promise<string | null> {
  try {
    const key      = await _getSessionKey()
    const combined = Uint8Array.from(atob(ciphertext), (c) => c.charCodeAt(0))
    const iv       = combined.slice(0, IV_LENGTH)
    const data     = combined.slice(IV_LENGTH)
    const plain    = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data)
    return new TextDecoder().decode(plain)
  } catch {
    // Decryption failure = wrong session key (e.g. new tab) — return null so
    // the caller can treat the data as absent and start fresh.
    return null
  }
}

/**
 * An async localStorage wrapper that transparently encrypts values with a
 * per-session AES-GCM key.  Compatible with Zustand's `persist` middleware.
 *
 * Usage:
 *   persist(storeFn, { name: 'my-store', storage: secureStorage })
 */
export const secureStorage = {
  async getItem(name: string): Promise<string | null> {
    const raw = localStorage.getItem(name)
    if (!raw) return null
    return _decrypt(raw)
  },

  async setItem(name: string, value: string): Promise<void> {
    const enc = await _encrypt(value)
    localStorage.setItem(name, enc)
  },

  removeItem(name: string): Promise<void> {
    localStorage.removeItem(name)
    return Promise.resolve()
  },
}
